import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { LoginParams, LoginResponse } from '../../models/auth.model';
import { finalize, map, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

const BASE_URL = environment.serverUrl;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #http = inject(HttpClient);
  #router = inject(Router);

  #isLoading = signal<boolean>(false);
  isLoading = this.#isLoading.asReadonly();

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  login(params: LoginParams): Observable<LoginResponse> {
    this.#isLoading.set(true);

    const endpoint = `${BASE_URL}/auth/login`;

    return this.#http.post<LoginResponse>(endpoint, params).pipe(
      map((response) => {
        this.saveToken(response);
        return response;
      }),
      finalize(() => this.#isLoading.set(false)),
    );
  }

  saveToken(response: LoginResponse): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, response.accessToken);
    sessionStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
  }

  getToken(): LoginResponse {
    return {
      accessToken: sessionStorage.getItem(this.ACCESS_TOKEN_KEY) || '',
      refreshToken: sessionStorage.getItem(this.REFRESH_TOKEN_KEY) || '',
    };
  }

  refreshToken(refreshToken: string): Observable<LoginResponse> {
    this.#isLoading.set(true);

    const endpoint = `${BASE_URL}/auth/refresh`;

    return this.#http.post<LoginResponse>(endpoint, { refreshToken }).pipe(
      map((response) => {
        this.saveToken(response);
        return response;
      }),
      finalize(() => this.#isLoading.set(false)),
    );
  }

  logout(): void {
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.#router.navigate(['/auth/login']);
  }
}
